// src/features/files/components/FileExplorer/modules/FileActionsMenu.jsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import {
  Delete as DeleteIcon,
  DriveFileMove as MoveIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  LockPerson as AccessIcon,
  LinkOff as RemoveIcon,
  MoreVert as MoreVertIcon,
  Public as ShareIcon,
  PublicOff as UnshareIcon,
} from '@mui/icons-material';

// `remove` unlinks a file from something (equipment, a task) without deleting it;
// `share` / `unshare` share a top-level folder with all departments, or stop;
// `access` opens who may see and change the item
const ACTIONS = {
  download: { icon: DownloadIcon, labelKey: 'files:actions.download' },
  rename: { icon: EditIcon, labelKey: 'files:actions.rename' },
  move: { icon: MoveIcon, labelKey: 'files:actions.move_to' },
  access: { icon: AccessIcon, labelKey: 'files:access.menu' },
  share: { icon: ShareIcon, labelKey: 'files:shared.share' },
  unshare: { icon: UnshareIcon, labelKey: 'files:shared.unshare' },
  remove: { icon: RemoveIcon, labelKey: 'files:actions.remove', color: 'error.main' },
  delete: { icon: DeleteIcon, labelKey: 'files:actions.delete', color: 'error.main' },
};

/** One file's actions, where `fileActions` is a list for every file or file => list */
export const actionsFor = (fileActions, file) => (typeof fileActions === 'function' ? fileActions(file) : fileActions);

const hasKey = (actions, key) => Boolean(actions?.some(a => (typeof a === 'string' ? a : a.key) === key));

/** Whether a file can be ticked for acting on several: when it can be moved or deleted */
export const canCheckFile = (fileActions, file) => {
  if (typeof fileActions !== 'function') return true;
  const actions = fileActions(file);
  return hasKey(actions, 'move') || hasKey(actions, 'delete');
};

/**
 * "⋯" button with a file's actions. `actions` lists keys of ACTIONS, or
 * { key, label } to relabel one (e.g. remove → "Fjern fra udstyr").
 * Picking one calls `onAction(key, file)`.
 */
export function FileActionsMenu({ file, actions, onAction, color }) {
  const { t } = useTranslation(['files']);
  const [anchor, setAnchor] = useState(null);
  if (!actions?.length) return null;

  const close = (event) => {
    event?.stopPropagation();
    setAnchor(null);
  };

  return (
    <>
      <Tooltip title={t('files:actions.more_actions')}>
        <IconButton
          size="small"
          aria-label={t('files:actions.more_actions')}
          aria-haspopup="menu"
          sx={{ color }}
          // The row itself selects or drags the file; the menu must do neither
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            setAnchor(event.currentTarget);
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        onClick={(event) => event.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {actions.map((action) => {
          const key = typeof action === 'string' ? action : action.key;
          const config = ACTIONS[key];
          if (!config) return null;
          const Icon = config.icon;
          const label = (typeof action === 'object' && action.label) || t(config.labelKey);
          return (
            <MenuItem
              key={key}
              onClick={(event) => {
                close(event);
                onAction(key, file);
              }}
              sx={config.color ? { color: config.color } : undefined}
            >
              <ListItemIcon sx={config.color ? { color: config.color } : undefined}>
                <Icon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{label}</ListItemText>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
